import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles, ArrowRight, CheckCircle2, Plane, Landmark, ShieldCheck,
    Handshake, TrendingUp, Calendar, Send,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const PILLARS = [
    { icon: Plane, title: "Investor Visa", body: "Investor permit applications, Investment Fiji registration, work permits for senior staff & family relocation — handled end-to-end." },
    { icon: Landmark, title: "Banking & Capital", body: "Pre-vetted bank introductions and Reserve Bank of Fiji approvals so capital deploys without a 4-month delay." },
    { icon: ShieldCheck, title: "Regulatory Approvals", body: "FIRCA, FRCS, RBF, council planning — every approval sequenced and lodged before you commit capital." },
    { icon: Handshake, title: "Joint Ventures", body: "Match your capital to landowners, mataqali, or businesses — and structure the JV so it survives audit, exit, and dispute." },
    { icon: TrendingUp, title: "Tax Incentives", body: "Tourism Hotel Aid Act, SLIP, Capital Investment Incentive — capture every tax holiday and concession Fiji offers." },
];

const QUIZ_QUESTIONS = [
    {
        id: "thesis",
        q: "What's your investment thesis?",
        options: [
            { value: "tourism", label: "Tourism — resort, hotel, eco-lodge" },
            { value: "residential", label: "Residential — subdivision, housing development" },
            { value: "land", label: "Land — buy-and-hold, lease, freehold" },
            { value: "business", label: "Business acquisition — buy an existing Fiji company" },
            { value: "exploring", label: "Just exploring options" },
        ],
    },
    {
        id: "capital",
        q: "What's your investable capital range?",
        options: [
            { value: "under-250k", label: "Under USD 250k" },
            { value: "250k-1m", label: "USD 250k – 1m" },
            { value: "1m-5m", label: "USD 1m – 5m" },
            { value: "5m-plus", label: "USD 5m+" },
        ],
    },
    {
        id: "timeline",
        q: "When do you want to deploy?",
        options: [
            { value: "now", label: "Ready now" },
            { value: "3-months", label: "Within 3 months" },
            { value: "6-months", label: "Within 6 months" },
            { value: "exploring", label: "Just exploring" },
        ],
    },
    {
        id: "visa",
        q: "Do you need a Fiji investor visa?",
        options: [
            { value: "yes", label: "Yes — I'll be relocating or visiting often" },
            { value: "maybe", label: "Possibly — depends on the structure" },
            { value: "no", label: "No — investing remotely" },
        ],
    },
    {
        id: "fiji-experience",
        q: "Have you invested in Fiji before?",
        options: [
            { value: "yes", label: "Yes — I have existing investments" },
            { value: "no", label: "No — first time" },
        ],
    },
];

export default function Invest() {
    const [step, setStep] = useState(0);  // 0 = hero, 1..5 = quiz questions, 6 = capture, 7 = done
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState({ full_name: "", email: "", phone: "", country: "" });
    const [submitting, setSubmitting] = useState(false);

    const totalSteps = QUIZ_QUESTIONS.length + 1;  // +1 for contact capture
    const inQuiz = step >= 1 && step <= QUIZ_QUESTIONS.length;
    const inCapture = step === QUIZ_QUESTIONS.length + 1;

    const tier = computeTier(answers);

    const submit = async (e) => {
        e.preventDefault();
        if (!contact.full_name || !contact.email) {
            toast.error("Name and email required");
            return;
        }
        setSubmitting(true);
        try {
            // Map to existing /jv-leads endpoint with structured payload
            await api.post("/jv-leads", {
                lead_type: "consultation",
                full_name: contact.full_name,
                email: contact.email,
                phone: contact.phone,
                message: `[Invest Quiz] thesis=${answers.thesis} | capital=${answers.capital} | timeline=${answers.timeline} | visa=${answers.visa} | experience=${answers["fiji-experience"]} | recommended_tier=${tier.id} | country=${contact.country}`,
                source: "invest_quiz",
            });
            setStep(QUIZ_QUESTIONS.length + 2);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not submit");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="invest-page" className="bg-background min-h-screen">
            {step === 0 && (
                <HeroSection onStart={() => setStep(1)} />
            )}

            {inQuiz && (
                <QuizSection
                    question={QUIZ_QUESTIONS[step - 1]}
                    answer={answers[QUIZ_QUESTIONS[step - 1].id]}
                    onAnswer={(v) => {
                        setAnswers({ ...answers, [QUIZ_QUESTIONS[step - 1].id]: v });
                        setStep(step + 1);
                    }}
                    onBack={() => setStep(step - 1)}
                    progress={(step / totalSteps) * 100}
                    stepNum={step}
                    totalSteps={totalSteps}
                />
            )}

            {inCapture && (
                <CaptureSection
                    contact={contact}
                    setContact={setContact}
                    tier={tier}
                    onSubmit={submit}
                    onBack={() => setStep(step - 1)}
                    submitting={submitting}
                    progress={100}
                />
            )}

            {step === QUIZ_QUESTIONS.length + 2 && (
                <DoneSection contact={contact} tier={tier} />
            )}
        </div>
    );
}

function HeroSection({ onStart }) {
    return (
        <>
            <section className="relative overflow-hidden bg-ocean-deep-grad text-white py-24 lg:py-32">
                <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-turquoise/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-6">
                        <Sparkles className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">For Foreign Investors</span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] mb-6">
                        Invest in Fiji.
                        <br />
                        <span className="italic text-turquoise">End-to-end.</span>
                    </h1>
                    <p className="font-app text-base sm:text-lg lg:text-xl text-white/85 leading-relaxed max-w-3xl mx-auto mb-10">
                        From investor visa to property keys-in-hand. We coordinate every step
                        — immigration, banking, regulatory approvals, joint ventures, ministry-level
                        government liaison — so capital deploys in weeks, not 12 months.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button size="lg" onClick={onStart} data-testid="invest-start-quiz" className="bg-coral hover:bg-coral-deep text-white text-base px-8 h-14">
                            Start the 60-second quiz <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <Button size="lg" asChild variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white text-base px-8 h-14">
                            <a href="https://cal.com/alvinkumar/general-consultation?utm_source=bularealestatefiji&utm_medium=invest_page&utm_campaign=hero" target="_blank" rel="noopener noreferrer" data-testid="invest-book-call">
                                <Calendar className="h-4 w-4 mr-2" />Book a strategy call
                            </a>
                        </Button>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-3 text-foreground text-center">
                        Five pillars. One coordinated team.
                    </h2>
                    <p className="font-app text-base text-muted-foreground text-center max-w-2xl mx-auto mb-12">
                        Each foreign-investor engagement at Bula Real Estate Fiji touches every one of these. None of them in isolation work.
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {PILLARS.map((p) => (
                            <Card key={p.title} className="p-5 bg-card hover:border-coral/50 transition">
                                <div className="h-10 w-10 rounded-lg bg-coral/10 grid place-items-center mb-3">
                                    <p.icon className="h-5 w-5 text-coral" />
                                </div>
                                <h3 className="font-display text-base font-bold mb-2 text-foreground">{p.title}</h3>
                                <p className="font-app text-xs text-muted-foreground leading-relaxed">{p.body}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-12 bg-secondary/40 dark:bg-card/40 border-y border-border">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <p className="font-app text-base sm:text-lg text-foreground italic mb-3">
                        "Most foreign investors give up halfway through Fiji's government maze. We don't let that happen."
                    </p>
                    <p className="font-app text-xs text-muted-foreground uppercase tracking-wider">— The Bula Real Estate Fiji Promise</p>
                </div>
            </section>

            <section className="py-16 lg:py-20">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <h2 className="font-display text-2xl lg:text-3xl font-bold mb-3 text-foreground">
                        Ready to see if Fiji is right for you?
                    </h2>
                    <p className="font-app text-muted-foreground mb-6">
                        Take the 60-second quiz. We'll match you to the right service tier and reply within one business day with a tailored roadmap.
                    </p>
                    <Button size="lg" onClick={onStart} className="bg-coral hover:bg-coral-deep text-white">
                        Start the quiz <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                </div>
            </section>
        </>
    );
}

function ProgressBar({ progress }) {
    return (
        <div className="h-1 bg-secondary/50 dark:bg-card overflow-hidden">
            <div className="h-full bg-coral transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
    );
}

function QuizSection({ question, answer, onAnswer, onBack, progress, stepNum, totalSteps }) {
    return (
        <>
            <ProgressBar progress={progress} />
            <section className="py-16 lg:py-24">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-xs font-app uppercase tracking-wider text-muted-foreground mb-4">
                        Question {stepNum} of {totalSteps}
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-8 text-foreground" data-testid={`quiz-question-${question.id}`}>
                        {question.q}
                    </h2>
                    <div className="space-y-2.5">
                        {question.options.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => onAnswer(opt.value)}
                                data-testid={`quiz-option-${question.id}-${opt.value}`}
                                className={`w-full text-left p-4 rounded-lg border-2 transition font-app text-sm sm:text-base ${
                                    answer === opt.value
                                        ? "border-coral bg-coral/10 text-foreground"
                                        : "border-border bg-card hover:border-coral/50 hover:bg-secondary/40 text-foreground"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <div className="mt-8 flex justify-between">
                        <Button variant="ghost" onClick={onBack} disabled={stepNum === 1} className="font-app text-muted-foreground">
                            ← Back
                        </Button>
                    </div>
                </div>
            </section>
        </>
    );
}

function CaptureSection({ contact, setContact, tier, onSubmit, onBack, submitting, progress }) {
    return (
        <>
            <ProgressBar progress={progress} />
            <section className="py-16 lg:py-24">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-xs font-app uppercase tracking-wider text-muted-foreground mb-4">Final step</div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-3 text-foreground">Where do we send your roadmap?</h2>
                    <p className="font-app text-muted-foreground mb-6">
                        Based on your answers, we'll match you to the <span className="text-coral font-semibold">{tier.label}</span> service tier and reply within one business day.
                    </p>

                    <Card className="p-5 mb-6 bg-coral/5 dark:bg-coral/10 border-coral/30" data-testid="invest-tier-preview">
                        <div className="flex items-start gap-3">
                            <Sparkles className="h-5 w-5 text-coral mt-0.5 shrink-0" />
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-coral font-app font-bold mb-1">Recommended tier</div>
                                <div className="font-display text-xl font-bold text-foreground mb-1">{tier.label}</div>
                                <p className="font-app text-sm text-muted-foreground">{tier.body}</p>
                            </div>
                        </div>
                    </Card>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="invest-capture-form">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Full name *</Label>
                                <Input data-testid="invest-name" value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Email *</Label>
                                <Input data-testid="invest-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Phone</Label>
                                <Input data-testid="invest-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="+1..." className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Country</Label>
                                <Input data-testid="invest-country" value={contact.country} onChange={(e) => setContact({ ...contact, country: e.target.value })} className="bg-card text-foreground" />
                            </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button type="button" variant="ghost" onClick={onBack} className="font-app text-muted-foreground">← Back</Button>
                            <Button type="submit" disabled={submitting} data-testid="invest-submit-btn" className="flex-1 bg-coral hover:bg-coral-deep text-white">
                                <Send className="h-4 w-4 mr-2" />
                                {submitting ? "Sending…" : "Get my roadmap"}
                            </Button>
                        </div>
                    </form>
                </div>
            </section>
        </>
    );
}

function DoneSection({ contact, tier }) {
    return (
        <section className="py-20 lg:py-28">
            <div className="max-w-2xl mx-auto px-4 text-center">
                <CheckCircle2 className="h-16 w-16 text-fiji-green mx-auto mb-5" />
                <h2 className="font-display text-3xl lg:text-4xl font-bold mb-3 text-foreground" data-testid="invest-done">Vinaka, {contact.full_name.split(" ")[0]}.</h2>
                <p className="font-app text-base text-muted-foreground mb-8">
                    Your <span className="text-coral font-semibold">{tier.label}</span> roadmap is being prepared. Watch your inbox at <strong className="text-foreground">{contact.email}</strong> for a tailored response within one business day.
                </p>
                <div className="bg-secondary/40 dark:bg-card/50 border border-border rounded-lg p-5 mb-6 text-left">
                    <div className="font-display text-base font-bold mb-2 text-foreground">Want to skip the wait?</div>
                    <p className="font-app text-sm text-muted-foreground mb-3">
                        Book a 30-minute strategy call directly on our calendar. We'll walk you through your roadmap live.
                    </p>
                    <Button asChild className="bg-coral hover:bg-coral-deep text-white">
                        <a href="https://cal.com/alvinkumar/general-consultation?utm_source=bularealestatefiji&utm_medium=invest_done&utm_campaign=quiz_completion" target="_blank" rel="noopener noreferrer">
                            <Calendar className="h-4 w-4 mr-2" /> Book a strategy call
                        </a>
                    </Button>
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                    <Button asChild variant="outline">
                        <Link to="/marketplace">Browse marketplace</Link>
                    </Button>
                    <Button asChild variant="outline">
                        <Link to="/jv">View Joint Ventures</Link>
                    </Button>
                </div>
            </div>
        </section>
    );
}

function computeTier(a) {
    const cap = a.capital;
    if (cap === "5m-plus") return { id: "developer", label: "Developer", body: "End-to-end project orchestration with ministry-level liaison." };
    if (cap === "1m-5m" || cap === "250k-1m") return { id: "investor", label: "Investor", body: "Full investor onboarding — visa, banking, property/JV match, regulatory approvals — 90 days white-glove." };
    return { id: "explorer", label: "Explorer", body: "Strategic shortlist + 30-min consultation + Foreign Investor's Guide to Fiji." };
}
